import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { getActiveChild, type SidebarNavFeature } from "@/config/sidebar-nav";
import { useActiveSidebarNavFeature } from "@/hooks/use-sidebar-nav-features";
import { cn } from "@/lib/utils";

/**
 * The segmented tab row under the top bar from the platform sketch: the
 * sub-pages of the active nav group (Team: Team, Medarbetare, Check in, …;
 * Underlag: Bruttovinst, Jämförelsedata, Data). The URL is the only state,
 * so each tab is a router link. Renders nothing for a group with fewer than
 * two sub-pages, so Idag and Agera keep a clean top edge. Tokens only.
 */
export function ShellSubTabs({ className }: { className?: string }) {
  const feature = useActiveSidebarNavFeature();
  const { pathname } = useLocation();
  if (!feature) return null;
  return <SubTabs feature={feature} pathname={pathname} className={className} />;
}

function SubTabs({
  feature,
  pathname,
  className,
}: {
  feature: SidebarNavFeature;
  pathname: string;
  className?: string;
}) {
  const { t } = useTranslation("nav");
  const tabs = feature.children ?? [];
  if (tabs.length < 2) return null;
  const active = getActiveChild(feature, pathname)?.url ?? feature.url;

  return (
    <nav
      aria-label={t(feature.titleKey)}
      data-testid="shell-sub-tabs"
      className={cn(
        "inline-flex max-w-full items-center gap-0.5 overflow-x-auto rounded-g-sm bg-g-bg-track p-1",
        className,
      )}
    >
      {tabs.map((tab) => {
        const current = tab.url === active;
        return (
          <Link
            key={tab.url}
            to={tab.url}
            aria-current={current ? "page" : undefined}
            className={cn(
              "inline-flex h-8 shrink-0 items-center whitespace-nowrap rounded-g-xs px-4 text-[13.5px] transition-colors duration-150 ease-out",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-g-ring",
              current
                ? "bg-g-bg-card font-[650] text-g-ink-900 shadow-g-card"
                : "font-medium text-g-ink-500 hover:text-g-ink-900",
            )}
          >
            {t(tab.titleKey)}
          </Link>
        );
      })}
    </nav>
  );
}
